import React, { ReactElement, ReactNode } from 'react';
import classNames from 'classnames/bind';
import { lruMemoize } from 'reselect';
// import ImgFallback from 'src/fleek/components/common/ImgFallback';
import ImgFallback from '../../../fleek/components/common/ImgFallback';
import Icon from '../../atoms/Icon';
import CartIcon from '../../molecules/CartIcon';
import { getColor } from '../../utils/style';
import Header, { Config } from './Header';
import { getCartUrl } from '../../utils/cartUtils';
import styles from './PlpHeader.pcss';
import { getDecodedString } from '../../../modules/ajio-util/stringDecoder';
import { formatCount, isTotalCountVisible } from '../../../modules/ajio-util/countDisplay';
import { closeScreenOnMyJioAction } from '../../utils/myJioUtils';
import { gtmEventPush, gtmEventPushPLP, segmentEventPush } from '../../../modules/ga/GTMdatalayer';

const cx = classNames.bind(styles);

const getStyles = lruMemoize(
  (fontSize: string, color: string, fontWeight: import('../../atoms/Icon/Icon').FontWeight) => ({
    fontSize,
    color,
    fontWeight
  })
);

const getHeaderIcon = (
  icon: string,
  weight: import('../../atoms/Icon/Icon').FontWeight,
  sisIconColor: string,
  className: string | undefined,
  fontSize: string | undefined,
  id: string | undefined,
  ariaLabel: string = '',
  role:string = 'button'
): ReactNode => {
  const iconColor = sisIconColor || '#202020';
  return (
    <div className={className}>
      <Icon ariaLabel={ariaLabel} icon={icon} styles={getStyles(fontSize, iconColor, weight)} id={id} role={role}/>
    </div>
  );
};
const getPlpHeaderName = (freeTextSearch, scrollProductCount,totalResults, textColor: string, minProductsCount, brandDetail, trendBasedDiscoveryFlag,trendName, totalCountDisplay): JSX.Element => {
  const searchParams = new URLSearchParams(window.location.search);
  const isBlp = searchParams.get('isBlp');
  const isCuratedPLP = window.location.pathname.includes('/s/');
  //this is used in season Essential redirect. There we don't want 
  // to take the brand name from the cache data. As caches has stale data
  const noCache = Boolean(searchParams.get('noCache'));
  let headerTitle = freeTextSearch;
  if(noCache){
    headerTitle = freeTextSearch;
  } else if(trendBasedDiscoveryFlag?.master && trendName){
    headerTitle = trendName;
  } else if(brandDetail && brandDetail?.name ){
    if(isBlp || isCuratedPLP ){
      headerTitle = brandDetail?.name
    }else{
      headerTitle = `${brandDetail.name} ${freeTextSearch}`;
    }
  }

  const { enabled: countEnabled = false, show: countShow = false, ranges: countRanges = [] } =
    totalCountDisplay || {};
  const defaultCountText =
    totalResults === 1
      ? `${totalResults} Product`
      : `${
          scrollProductCount > minProductsCount
            ? `${scrollProductCount}/${totalResults}`
            : totalResults
        } Products`;
  const showCount = isTotalCountVisible(totalCountDisplay);
  const countText =
    countEnabled && countShow
      ? `${formatCount(totalResults, countRanges)} ${totalResults === 1 ? 'Product' : 'Products'}`
      : defaultCountText;

  const headerDetail = (
    <div className={cx('headerName')}>
    <span
      className={cx(!trendName && 'is-capitalized', 'headertext')}
      style={getColor({ color: textColor || '#202020' })}
      id="plpBrandName"
      role='heading'
      aria-level={2}
    >
      <strong>{headerTitle}</strong>
    </span>
    {totalResults > 0 && showCount ? (
      <span
        className={cx('is-capitalized', 'subheadertext')}
        style={{ color: textColor || '#6D6D6D' }}
        id="plpNumberOfProducts"
      >
        {countText}
      </span>
    ) : null}
  </div>
  )
  if (brandDetail && Object.keys(brandDetail)?.length >0 && !noCache) {
    return (
      <div className={cx('header-detail-container')}>
        {brandDetail && (
          <ImgFallback
            fallback="logo"
            className={cx('brand-logo')}
            src={brandDetail.logo}
            alt="brand logo"
          />
        )}
        {headerDetail}
      </div>
    );
  }

  return headerDetail;
};

const BackIcon = ({ sisIconColor = '#202020' }: Record<string, string>): JSX.Element => (
  <div className={cx('backIconWrapper')}>
    <button className={cx('backIcon', 'backIconButton')} aria-label="Back">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18.527"
        height="16.286"
        viewBox="0 0 18.527 16.286"
      >
        <path
          fill={sisIconColor}
          d="M19.5-45.02a1.247 1.247 0 0 0-1.247-1.247H5.382l5.068-4.725a1.247 1.247 0 0 0 .4-1.208 1.247 1.247 0 0 0-.869-.929 1.247 1.247 0 0 0-1.232.317l-7.384 6.879a1.247 1.247 0 0 0-.4.912 1.247 1.247 0 0 0 .4.912l7.383 6.881a1.247 1.247 0 0 0 1.762-.061 1.247 1.247 0 0 0-.061-1.762l-5.067-4.722h12.867A1.247 1.247 0 0 0 19.5-45.02z"
          transform="translate(-.968 53.178)"
        />
      </svg>
    </button>
  </div>
);

function plpConfig(plpHeaderNode = '', setSearch, sisIconColor: string, toggleLHN, isNewNavigation=false, isFleekBrandPLP=false): Config[] {
  const cartUrl = getCartUrl();
  return [
    {
      component: <BackIcon sisIconColor={sisIconColor} />,
      width: 15,
      action: () => {
        closeScreenOnMyJioAction();
      }
    },
    {
      component: getHeaderIcon(
        'icon-Hamburger',
        'bold',
        sisIconColor,
        styles.hamburger,
        '2rem',
        '',
        'Open Menu bar'
      ),
      width: 10,
      action: toggleLHN,
      testId: 'open-search-category',
      type: 'hamburger-icon'
    },
    {
      component: plpHeaderNode,
      width: isFleekBrandPLP ? 65 : isNewNavigation ? 55 : 45
    },
    {
      component: getHeaderIcon('icon-Search', 'normal', sisIconColor, '', '2.8rem', 'searchIcon', 'Search Items'),
      width: 10,
      action: () => {
        setSearch();
        gtmEventPush({
          event: 'search_bar_interactions',
          event_category: 'search bar interactions',
          event_action: 'search icon click',
          page_type: 'plp screen'
        });

      },
      type: 'search-icon'
    },
    {
      component: getHeaderIcon(
        'icon-Wishlist',
        'normal',
        sisIconColor,
        '',
        '2.9rem',
        'wishListIcon',
        'Open wishlist',
        'link'
      ),
      width: 10,
      action: () => {
        gtmEventPush({
          event: 'buttonTap',
          event_category: 'buttonTap',
          event_action: 'Header Click',
          event_label: 'Closet Button'
        });

      

        window.location.href = '/wishlist';
        gtmEventPushPLP({
          event: 'plp_navigation_interaction',
          event_category: 'plp_interaction',
          event_action: 'wishlist click',
          event_label: 'wishlist',
          store_type: 'ajio',
          page_type: window.pageType || ''
        }, 'plp_navigation_interaction', true);
      }
    },
    {
      component: <CartIcon sisIconColor={sisIconColor} id="bagIcon" />,
      width: 10,
      action: () => {
        gtmEventPush({
          event: 'buttonTap',
          event_category: 'buttonTap',
          event_action: 'Header Click',
          event_label: 'Bag'
        });

       

        window.location.href = cartUrl;
        gtmEventPushPLP({
          event: 'plp_navigation_interaction',
          event_category: 'plp_interaction',
          event_action: 'cart icon click',
          event_label: 'cart',
          store_type: 'ajio',
          page_type: window.pageType || ''
        }, 'plp_navigation_interaction', true);
      }
    }
  ];
}

function PlpHeader(props): ReactElement {
  const {
    freeTextSearch = '',
    totalResults = '',
    setSearch,
    sisStyles = {},
    sisFlag,
    openCategories,
    scrollProductCount = 0,
    searchedData = '',
    changeView,
    isRevampedNavigationEnable=false,
    fleek = false,
    brandDetails,
    isFleekBrandPLP=false,
    trendBasedDiscoveryFlag= {},
    trendName = '',
    totalCountDisplay = {}
  } = props;
  let autoSearchVal = searchedData || freeTextSearch;
  autoSearchVal = getDecodedString(autoSearchVal) || '';
  const { colorTheme, headerIconColor = '', textColor } = sisStyles;
  const fleekColor = { text: '#FFF', background: '#060C10' };
  const sisIconColor = fleek ? fleekColor.text : sisFlag ? headerIconColor : '';
  const minProductsCount = changeView ? 2 : 4;
  const headerTextColor = fleek ? fleekColor.text : textColor;
  const isNewNavigation = isRevampedNavigationEnable || fleek;
  const plpHeaderNode = getPlpHeaderName(autoSearchVal, scrollProductCount, totalResults, headerTextColor, minProductsCount, brandDetails,trendBasedDiscoveryFlag,trendName, totalCountDisplay);
  const newPlpConfig = plpConfig(plpHeaderNode, setSearch, sisIconColor, openCategories, isNewNavigation, isFleekBrandPLP);

  const filterByType = (type) => (items) => items.filter(item => item?.type !== type);
  const filterHamburgerIcon = filterByType('hamburger-icon');
  const filterSearchIcon = filterByType('search-icon');

  const newPlpConfigResp = isNewNavigation ? filterHamburgerIcon(newPlpConfig) : newPlpConfig;
  const newPLPConfigFiltered = isFleekBrandPLP ? filterSearchIcon(newPlpConfigResp) : newPlpConfigResp;

  return (
    <>
      <Header headerConfig={newPLPConfigFiltered} backgroundColor={fleek ? fleekColor.background : sisFlag ? colorTheme : ''} />
    </>
  );
}

export default PlpHeader;
